/**
 * Drag-and-drop avatar upload widget for Settings.
 * Uploads to /api/upload/avatar (object storage), then writes the returned
 * URL into the AVATAR_URL setting server-side so branding refreshes on
 * next page load.
 */
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, X } from "lucide-react";
import { uploadAvatar } from "@/lib/api";

export default function AvatarDropzone({ current, onChange }) {
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const backend = process.env.REACT_APP_BACKEND_URL;
  const previewUrl = current?.startsWith("/api/")
    ? `${backend}${current}`
    : current;

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please drop a JPG / PNG / WEBP image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image is over 5 MB — please compress first");
      return;
    }
    setBusy(true);
    try {
      const r = await uploadAvatar(file);
      onChange?.(r.url);
      toast.success("Portrait uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="avatar-dropzone">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          doUpload(e.dataTransfer.files?.[0]);
        }}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-sm p-6 flex items-center gap-4 cursor-pointer transition-colors ${
          dragging ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#eaecf0] hover:border-[#0A0A0A]"
        }`}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Current portrait"
            className="w-16 h-16 rounded-full object-cover border border-[#eaecf0]"
            onError={(e) => { e.currentTarget.style.opacity = "0.4"; }}
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-[#F4F4F5] flex items-center justify-center">
            <Upload className="w-5 h-5 text-[#999]" strokeWidth={1.5} />
          </div>
        )}
        <div className="flex-1">
          <div className="text-sm text-[#101828]">
            {busy ? "Uploading…" : dragging ? "Drop to upload" : "Drag & drop, or click to choose"}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[#999] mt-1 font-mono">
            JPG · PNG · WEBP · max 5 MB · square works best
          </div>
        </div>
        {busy && <Loader2 className="w-4 h-4 animate-spin text-[#666]" />}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => doUpload(e.target.files?.[0])}
        className="hidden"
        data-testid="avatar-file-input"
      />
      {current && (
        <button
          type="button"
          onClick={() => onChange?.("")}
          className="text-[10px] uppercase tracking-wider text-[#999] hover:text-[#BE123C] font-mono inline-flex items-center gap-1"
          data-testid="avatar-remove"
        >
          <X className="w-3 h-3" />
          Remove portrait
        </button>
      )}
    </div>
  );
}
